import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { extractErrorMessage } from "@/api/client";
import {
  listingAgentResearchApi,
  type ListingAgentResearchPastedFindingsPayload,
  type ListingAgentResearchRunDto,
} from "@/api/listingAgentResearch";
import type { ListingAgentPasteCandidateDto } from "@/api/listingAgentPaste";
import { Button, Label, Textarea, useToast } from "@/design-system/components/ui";
import { listingAgentResearchKeys } from "@/hooks/buyers/listingAgentResearchKeys";
import { DetailSection } from "../../detailBits";
import { AgentPasteToFill } from "../agentPaste/AgentPasteToFill";
import { agentResearchCopy } from "./agentResearchCopy";

/** The paste candidate as the pasted-findings request — the same thirteen
 *  fields, Unknown phone type sent as no type. */
export function pastedFindingsPayload(c: ListingAgentPasteCandidateDto): ListingAgentResearchPastedFindingsPayload {
  return {
    firstName: c.firstName ?? null,
    lastName: c.lastName ?? null,
    email: c.email ?? null,
    phone: c.phone ?? null,
    phoneType: c.phoneType && c.phoneType !== "Unknown" ? c.phoneType : null,
    firmName: c.firmName ?? null,
    title: c.title ?? null,
    city: c.city ?? null,
    state: c.state ?? null,
    firmGeneralEmail: c.firmGeneralEmail ?? null,
    firmMainPhone: c.firmMainPhone ?? null,
    contactFormUrl: c.contactFormUrl ?? null,
    profileUrl: c.profileUrl ?? null,
  };
}

/**
 * ④ Your turn (D15) — what the specialist found themselves: notes on the run,
 * PASTE TO FILL (the shared paste box) turned into findings marked "Confirmed (by
 * you)", and RUN AGAIN WITH WHAT I FOUND, which starts a new run from this one
 * (earlier dead ends left out, R2). Every control is Listings.Manage only.
 */
export function YourTurnStage({
  listingId,
  run,
  canManage,
  hasActiveRun,
  onRanAgain,
}: {
  listingId: string;
  run: ListingAgentResearchRunDto | null | undefined;
  canManage: boolean;
  hasActiveRun: boolean;
  onRanAgain: (run: ListingAgentResearchRunDto) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [notes, setNotes] = useState(run?.notes ?? "");
  const [pasteOpen, setPasteOpen] = useState(false);

  // Another run selected: its own notes, not the last one's.
  useEffect(() => setNotes(run?.notes ?? ""), [run?.id, run?.notes]);

  const saveNotes = useMutation({
    mutationFn: () => listingAgentResearchApi.saveNotes(listingId, run!.id, notes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listingAgentResearchKeys.forListing(listingId) });
      toast.success(notes.trim() ? "Notes saved." : "Notes cleared.");
    },
  });

  const paste = useMutation({
    mutationFn: (c: ListingAgentPasteCandidateDto) =>
      listingAgentResearchApi.addPastedFindings(listingId, run!.id, pastedFindingsPayload(c)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listingAgentResearchKeys.forListing(listingId) });
      setPasteOpen(false);
      toast.success("Added to the findings as confirmed by you — review them on Findings.");
    },
  });

  const runAgain = useMutation({
    mutationFn: () => listingAgentResearchApi.start(listingId, { priorRunId: run!.id, notes: notes.trim() || null }),
    onSuccess: (next) => {
      void qc.invalidateQueries({ queryKey: listingAgentResearchKeys.forListing(listingId) });
      onRanAgain(next);
    },
  });

  if (!run) {
    return <p className="text-small text-gray-mid m-0">{agentResearchCopy.noRunYet}</p>;
  }

  if (!canManage) {
    return (
      <DetailSection title="Notes">
        <p className="text-small text-onyx m-0 whitespace-pre-wrap" data-testid="agent-research-notes-read">
          {run.notes || "No notes on this run."}
        </p>
      </DetailSection>
    );
  }

  const error = saveNotes.error ?? paste.error ?? runAgain.error;

  return (
    <div className="grid gap-space-5" data-testid="agent-research-your-turn">
      <DetailSection title="Your notes">
        <div className="grid gap-space-2">
          <Label htmlFor="agent-research-notes">What you found, or where to look next</Label>
          <Textarea
            id="agent-research-notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="agent-research-notes"
          />
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => saveNotes.mutate()}
              disabled={saveNotes.isPending || notes === (run.notes ?? "")}
              data-testid="agent-research-notes-save"
            >
              Save notes
            </Button>
          </div>
        </div>
      </DetailSection>

      <DetailSection title="Paste what you found">
        <div className="grid gap-space-2">
          <p className="text-caption text-gray-mid m-0">
            Paste a broker profile, a listing page or an email signature, or a screenshot of one. What you pick
            is added to this run's findings as confirmed by you.
          </p>
          <AgentPasteToFill
            open={pasteOpen}
            onOpenChange={setPasteOpen}
            onPick={(choice) => paste.mutate(choice.candidate)}
            disabled={paste.isPending}
            testIdPrefix="agent-research-paste"
          />
        </div>
      </DetailSection>

      <DetailSection title="Run again">
        <div className="grid gap-space-2">
          <p className="text-caption text-gray-mid m-0">{agentResearchCopy.runAgainNote}</p>
          {hasActiveRun ? (
            <p className="text-small text-gray-mid m-0" data-testid="agent-research-run-again-blocked">
              {agentResearchCopy.alreadyRunning}
            </p>
          ) : (
            <div>
              <Button onClick={() => runAgain.mutate()} disabled={runAgain.isPending} data-testid="agent-research-run-again">
                {runAgain.isPending ? "Starting…" : "Run again with what I found"}
              </Button>
            </div>
          )}
        </div>
      </DetailSection>

      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {extractErrorMessage(error)}
        </p>
      )}
    </div>
  );
}
